import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, X } from "lucide-react";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { api, type ApiResponse } from "@/api/client";
import { getUserMessage } from "@/lib/errorMessages";
import { withMediaTag } from "./mediaTags";

/**
 * Tagy fotky přímo v panelu velkého prohlížeče, v okně výběru fotek i na stránce Média.
 *
 * Redakce fotky vybírá v editorech (zájezd, hotel, destinace…) a do sekce Média nechodí;
 * detail fotky okno výběru záměrně nenabízí (řez 05, AK9: z editoru se nemaže). Tag se
 * proto ukládá tady, hned po Enteru nebo křížku: PATCH nese jen `tags`, takže nepřepíše
 * ★ ani nic jiného. Hledání v knihovně i v okně výběru ho pak najde (podnět redakce
 * 30. 10. 2026).
 *
 * Volající dává `key={item.id}`: rozepsaný tag ani chyba nesmí přejít na další fotku.
 */
export default function MediaTagsField({
  item,
  onSaved,
}: {
  item: AdminMediaResponse;
  onSaved: (item: AdminMediaResponse) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [tags, setTags] = useState<readonly string[]>(item.tags);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(next: readonly string[]) {
    const previous = tags;
    setTags(next);
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch<ApiResponse<AdminMediaResponse>>(`/api/admin/upload/${item.id}`, {
        tags: next,
      });
      onSaved(res.data);
    } catch (err) {
      setTags(previous);
      setError(getUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function add() {
    if (saving) return;
    const next = withMediaTag(tags, input);
    setInput("");
    if (next) void save(next);
  }

  return (
    <div data-photo-viewer-tags="">
      <h3 className="mb-1 flex items-center gap-1.5 text-xs text-white/50">
        {t("mediaViewer.tags.label")}
        {saving && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
      </h3>
      {tags.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <li
              key={tag}
              data-photo-viewer-tag={tag}
              className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-xs"
            >
              {tag}
              <button
                type="button"
                title={t("mediaViewer.tags.remove", { tag })}
                aria-label={t("mediaViewer.tags.remove", { tag })}
                disabled={saving}
                onClick={() => void save(tags.filter((x) => x !== tag))}
                className="text-white/60 hover:text-white disabled:opacity-40"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-1.5">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          aria-label={t("mediaViewer.tags.label")}
          placeholder={t("mediaViewer.tags.placeholder")}
          className="min-w-0 flex-1 rounded border border-white/20 bg-white/10 px-2 py-1 text-sm text-white placeholder-white/40 focus:border-white/50 focus:outline-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={saving}
          title={t("mediaViewer.tags.add")}
          aria-label={t("mediaViewer.tags.add")}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-white/80 hover:bg-white/15 disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {error ? (
        <p role="alert" className="mt-1 text-xs text-red-300">
          {error}
        </p>
      ) : (
        <p className="mt-1 text-xs text-white/40">{t("mediaViewer.tags.hint")}</p>
      )}
    </div>
  );
}
